import { useEffect, useRef, useState } from 'react';
import Translate from '@carbon/icons-react/es/Translate';
import ChevronDown from '@carbon/icons-react/es/ChevronDown';

import { useI18n, LOCALES, type Locale, type MessageKey } from '../../i18n/index.js';

/** Code court affiché par langue (demande produit : FRA / ENG / UKR). */
const LANG_CODE: Record<Locale, string> = { fr: 'FRA', en: 'ENG', uk: 'UKR' };

/** Clé du nom complet de chaque langue, traduit dans la langue courante. */
const LANG_NAME_KEY: Record<Locale, MessageKey> = {
  fr: 'language.french',
  en: 'language.english',
  uk: 'language.ukrainian',
};

/**
 * Sélecteur de langue : **une seule** icône (globe) en en-tête qui ouvre un menu des langues
 * disponibles (FRA / ENG / UKR). Remplace l'ancien groupe de boutons, pour rester lisible quand
 * le nombre de langues grandit.
 */
export function LanguageMenu() {
  const { locale, setLocale, t } = useI18n();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Fermer au clic hors du menu et à Échap : un menu flottant qui reste ouvert est un piège.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="ddp-lang" ref={containerRef}>
      <button
        type="button"
        className="ddp-lang__trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('language.label')}
        title={t('language.label')}
        onClick={() => setOpen((value) => !value)}
      >
        <Translate size={20} />
        <span className="ddp-lang__code">{LANG_CODE[locale]}</span>
        <ChevronDown size={16} aria-hidden="true" />
      </button>

      {open && (
        <ul className="ddp-lang__menu" role="menu" aria-label={t('language.label')}>
          {LOCALES.map((code) => (
            <li key={code} role="none">
              <button
                type="button"
                role="menuitemradio"
                aria-checked={locale === code}
                className={`ddp-lang__item${locale === code ? ' ddp-lang__item--active' : ''}`}
                onClick={() => {
                  setLocale(code);
                  setOpen(false);
                }}
              >
                <span className="ddp-lang__item-code">{LANG_CODE[code]}</span>
                <span className="ddp-lang__item-name">{t(LANG_NAME_KEY[code])}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
